import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { deleteLegajo } from '@lib/services/padrones.service';

interface LegajoTarget {
  id: string;
  numero: string;
  familiares: number;
}

/**
 * Encapsula el flow "abrir modal → escribir número del legajo para confirmar
 * → ejecutar DELETE → cerrar". Se extrajo fuera de PadronDetallePage para
 * bajar la cognitive complexity del componente (Sonar). Devuelve un objeto
 * con todo lo que la página necesita renderizar el botón + modal.
 */
export function useEliminarLegajo(padronId: string) {
  const qc = useQueryClient();
  const [target, setTarget] = useState<LegajoTarget | null>(null);
  const [confirmText, setConfirmText] = useState('');
  const [error, setError] = useState<string | null>(null);

  const mut = useMutation({
    mutationFn: (id: string) => deleteLegajo(id),
    onSuccess: () => {
      setTarget(null);
      setConfirmText('');
      setError(null);
      qc.invalidateQueries({ queryKey: ['legajos', padronId] });
      qc.invalidateQueries({ queryKey: ['resumen-padron', padronId] });
      qc.invalidateQueries({ queryKey: ['padron', padronId] });
    },
    onError: (err: unknown) => {
      const e = err as { response?: { data?: { error?: { message?: string } } } };
      setError(e.response?.data?.error?.message ?? 'No se pudo eliminar el legajo.');
    },
  });

  const cancelar = () => {
    setTarget(null);
    setConfirmText('');
    setError(null);
  };

  const confirmar = () => {
    if (target) mut.mutate(target.id);
  };

  const puedeConfirmar = !!target && confirmText.trim() === target.numero && !mut.isPending;

  return {
    target,
    abrir: setTarget,
    confirmText,
    setConfirmText,
    error,
    cancelar,
    confirmar,
    puedeConfirmar,
    submitting: mut.isPending,
  };
}
